import {Animated, Dimensions, Image, ImageBackground, Platform, Pressable, StyleSheet, useWindowDimensions} from "react-native"
import {memo, PropTypes, propTypesExact, shapeComponent, ShapeComponent, Text, useBreakpoint, View} from "shared/base"
import React, {useEffect, useMemo} from "react"
import {SchoolClass, Subscription} from "models"
import useShowMenu, {breakpointsWithExtraMenuSize, breakpointsWithoutExtraMenuSize} from "./use-show-menu"
import ApiMakerConfig from "@kaspernj/api-maker/build/config"
import background from "./background.png"
import ClickOutsideView from "outside-eye/build/outside-view"
import CommandsPool from "@kaspernj/api-maker/build/commands-pool"
import Devise from "@kaspernj/api-maker/build/devise"
import {FrontLayoutContext} from "components/front-layout"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import isExpo from "@kaspernj/api-maker/build/is-expo"
import Link from "@kaspernj/api-maker/build/link"
import LogoImage from "./logo.svg"
import Menu from "./menu"
import ProTopBar from "components/user/pro/top-bar"
import ProUpgradeOverlay from "components/pro/upgrade-overlay"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import SignIn from "./sign-in"
import useAnythingPro from "components/subscriptions/use-anything-pro"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useEventListener from "ya-use-event-listener"
import useHasPro from "components/subscriptions/use-has-pro"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"
import WeekBar from "components/user/dashboard/week-bar"
import {WithDefaultStyle} from "@kaspernj/api-maker/build/utils/default-style"

const styles = StyleSheet.create({
  defaultText: {
    fontFamily: "HankenGrotesk"
  },
  imageBackground: {
    alignItems: "center",
    justifyContent: "center",
    minWidth: "100%",
    minHeight: "100vh"
  }
})

const defaultStyle = {
  Text: [styles.defaultText]
}

/**
 * @typedef {object} ChalkyLayoutProps
 * @property {unknown} active
 * @property {boolean} centered
 * @property {unknown} children
 * @property {unknown} dataSet
 * @property {unknown} headerTitle
 * @property {string} layout
 * @property {unknown} maxWidth
 * @property {boolean} menu
 * @property {unknown} proTrialTopBar
 * @property {boolean} redirectAfterSignIn
 * @property {boolean} requireUser
 * @property {unknown} schoolClass
 * @property {boolean} userMenu
 * @property {boolean} weekBar
 */

/**
 * @typedef {object} ChalkyLayoutState
 * @property {unknown} actions
 * @property {unknown} active
 * @property {unknown} background
 * @property {unknown} bottomBar
 * @property {unknown} bottomBarHeight
 * @property {unknown} centered
 * @property {unknown} className
 * @property {unknown} contentCentered
 * @property {boolean} darkBackground
 * @property {boolean} forceSignedOut
 * @property {unknown} headerRight
 * @property {unknown} headerStyle
 * @property {unknown} headerTitle
 * @property {unknown} headerTitleCentered
 * @property {unknown} headTitle
 * @property {boolean} logo
 * @property {boolean} menu
 * @property {boolean} menuTriggered
 * @property {unknown} proTrialTopBar
 * @property {boolean} requireUser
 * @property {unknown} schoolClass
 * @property {boolean} seenExpiredTrialFirstVisitUpgradeOverlayThisSession
 * @property {boolean} showExpiredTrialFirstVisitUpgradeOverlay
 * @property {boolean} showUpgrade
 * @property {unknown} subHeader
 * @property {unknown} topLeft
 * @property {unknown} topRight
 * @property {boolean} userMenu
 * @property {boolean} weekBar
 */

/**
 * @extends {ShapeComponent<ChalkyLayoutProps, ChalkyLayoutState>}
 */
class ChalkyLayout extends ShapeComponent {
  /** @type {ChalkyLayoutState} */
  state = {
    background: null,
    bottomBar: null,
    bottomBarHeight: undefined,
    actions: null,
    active: null,
    centered: null,
    className: null,
    contentCentered: null,
    darkBackground: false,
    forceSignedOut: false,
    headTitle: null,
    headerRight: null,
    headerTitle: null,
    headerTitleCentered: null,
    headerStyle: null,
    logo: true,
    menu: true,
    menuTriggered: false,
    requireUser: false,
    schoolClass: null,
    seenExpiredTrialFirstVisitUpgradeOverlayThisSession: false,
    showExpiredTrialFirstVisitUpgradeOverlay: false,
    showUpgrade: false,
    subHeader: null,
    topLeft: null,
    topRight: null,
    proTrialTopBar: null,
    userMenu: true,
    weekBar: true
  }

  static defaultProps = {
    active: null,
    centered: true,
    children: null,
    dataSet: null,
    layout: "user",
    maxWidth: null,
    menu: true,
    redirectAfterSignIn: false,
    requireUser: true,
    schoolClass: null,
    proTrialTopBar: null,
    userMenu: true,
    weekBar: true
  }

  static propTypes = propTypesExact({
    active: PropTypes.string,
    centered: PropTypes.bool.isRequired,
    children: PropTypes.node,
    dataSet: PropTypes.object,
    headerTitle: PropTypes.string,
    layout: PropTypes.string.isRequired,
    maxWidth: PropTypes.number,
    menu: PropTypes.bool.isRequired,
    redirectAfterSignIn: PropTypes.bool.isRequired,
    requireUser: PropTypes.bool.isRequired,
    schoolClass: PropTypes.instanceOf(SchoolClass),
    proTrialTopBar: PropTypes.bool,
    userMenu: PropTypes.bool.isRequired,
    weekBar: PropTypes.bool.isRequired
  })

  proTopBarHeight = 0
  windowWidth = new Animated.Value(Dimensions.get("window").width)
  windowHeight = new Animated.Value(Dimensions.get("window").height)

  setup() {
    setLayout(this.p.layout)

    const currentUser = useCurrentUser()
    const params = useParams()
    const {schoolClass: routeSchoolClass} = useModel(SchoolClass, {
      match: {params},
      optional: true,
      preload: ["school.account", "school.country"],
      select: {
        Account: ["id"],
        Country: ["countryCode"],
        School: ["name"],
        SchoolClass: ["id", "schoolId"]
      }
    })
    const {expiredTrialSubscription, hasPro, loaded: hasProLoaded, userSubscriptionPlanIdentifier} = useHasPro({user: currentUser})
    const anythingProState = useAnythingPro({currentUser})
    const anythingPro = Boolean(
      hasPro ||
      Boolean(userSubscriptionPlanIdentifier) ||
      anythingProState?.anythingPro ||
      anythingProState?.hasProTrial ||
      anythingProState?.userProSubscriptions?.length > 0 ||
      anythingProState?.schoolsWithPro?.length > 0 ||
      anythingProState?.schoolClassesWithPro?.length > 0 ||
      anythingProState?.schoolsThroughClassWithSchoolPro?.length > 0
    )
    const {showMenu} = useShowMenu()


    if (isExpo) {
      const {height, width} = useWindowDimensions()

      this.height = height
      this.width = width
    }

    useEventListener(Dimensions, "change", this.tt.onDimensionsChange)

    useMemo(() => {
      ApiMakerConfig.setBreakpoints(showMenu ? breakpointsWithExtraMenuSize : breakpointsWithoutExtraMenuSize)
    }, [showMenu])

    useMemo(() => {
      if (this.s.forceSignedOut && Devise.isUserSignedIn()) {
        this.setState({forceSignedOut: false})
      }
    }, [currentUser?.id(), this.s.forceSignedOut])

    useEffect(() => {
      // This will be set instantly be useMemo instead

      return () => {
        ApiMakerConfig.setBreakpoints(breakpointsWithoutExtraMenuSize)
      }
    }, [])

    const expiredTrialPaywallShownAtFirstPlatformVisit = Boolean(expiredTrialSubscription?.expiredTrialPaywallShownAtFirstPlatformVisit?.())

    useEffect(() => {
      if (
        this.p.layout != "user" ||
        !currentUser?.id() ||
        hasProLoaded !== true ||
        hasPro !== false ||
        !expiredTrialSubscription ||
        expiredTrialPaywallShownAtFirstPlatformVisit ||
        this.s.seenExpiredTrialFirstVisitUpgradeOverlayThisSession ||
        this.s.showExpiredTrialFirstVisitUpgradeOverlay ||
        this.s.showUpgrade
      ) {
        return
      }

      this.setState({
        seenExpiredTrialFirstVisitUpgradeOverlayThisSession: true,
        showExpiredTrialFirstVisitUpgradeOverlay: true
      })
    }, [
      currentUser?.id(),
      expiredTrialPaywallShownAtFirstPlatformVisit,
      expiredTrialSubscription?.id?.(),
      hasPro,
      hasProLoaded,
      this.p.layout,
      this.s.seenExpiredTrialFirstVisitUpgradeOverlayThisSession,
      this.s.showExpiredTrialFirstVisitUpgradeOverlay,
      this.s.showUpgrade
    ])

    this.breakpoint = useBreakpoint()
    this.xsUp = this.breakpoint.xsUp
    this.xsDown = this.breakpoint.xsDown
    this.smUp = this.breakpoint.smUp
    this.smDown = this.breakpoint.smDown
    this.mdUp = this.breakpoint.mdUp
    this.mdDown = this.breakpoint.mdDown
    this.lgUp = this.breakpoint.lgUp
    this.lgDown = this.breakpoint.lgDown
    this.xlUp = this.breakpoint.xlUp
    this.xlDown = this.breakpoint.xlDown
    this.xxlUp = this.breakpoint.xxlUp
    this.xxlDown = this.breakpoint.xxlDown
    this.anythingPro = anythingPro
    this.anythingProState = anythingProState
    this.currentUser = currentUser
    this.hasPro = hasPro
    this.routeSchoolClass = routeSchoolClass
    this.showMenu = showMenu
    this.userSubscriptionPlanIdentifier = userSubscriptionPlanIdentifier

    useMemo(() => {
      if (Platform.OS == "web") {
        this.setDocumentTitle()
      }
    }, [this.s.headTitle, this.s.headerTitle])

    useMemo(() => {
      this.setSchoolAndClass()
    }, [this.p.schoolClass?.id(), this.s.schoolClass?.id()])

    useEffect(() => {
      return () => {
        CommandsPool.current().globalRequestData.school_id = null
        CommandsPool.current().globalRequestData.school_class_id = null
      }
    }, [])
  }

  setSchoolAndClass() {
    CommandsPool.current().globalRequestData.school_id = this.p.schoolClass?.schoolId() || this.s.schoolClass?.schoolId()
    CommandsPool.current().globalRequestData.school_class_id = this.p.schoolClass?.id() || this.s.schoolClass?.id()
  }

  render() { // eslint-disable-line complexity
    const {mdUp, showMenu, smUp, windowHeight, windowWidth, xlUp} = this.tt
    const currentUser = this.s.forceSignedOut ? null : this.tt.currentUser
    const {active, children, dataSet, maxWidth, requireUser} = this.p
    const {actions, bottomBar, bottomBarHeight, headerRight, menuTriggered, topLeft, topRight} = this.s
    const headerTitle = this.s.headerTitle || this.props.headerTitle
    const schoolClass = this.s.schoolClass || this.p.schoolClass || this.tt.routeSchoolClass

    let logo = true
    let menu = true
    let userMenu = true
    let weekBar = true

    if (!this.s.logo) logo = false
    if (!this.p.menu || !this.s.menu) menu = false
    if (!this.p.userMenu || !this.s.userMenu) userMenu = false
    if (!this.p.weekBar || !this.s.weekBar) weekBar = false

    const actualDataSet = useMemo(
      () => Object.assign(
        {component: "chalky-layout/image-background"},
        dataSet
      ),
      [dataSet]
    )

    let imageBackgroundStyle

    if (isExpo) {
      const {height, width} = this.tt

      imageBackgroundStyle = useMemo(() => ({width: "100%", height: "100%", minWidth: width, minHeight: height}), [width, height])
    } else {
      imageBackgroundStyle = styles.imageBackground
    }

    let centered = false
    let showSignIn = false
    let proTrialTopBar = true
    let actualShowMenu, logoAlign, logoHeight, logoWidth

    if (this.p.centered === false || this.s.centered === false) {
      centered = false
    } else if (this.p.centered || this.s.centered) {
      centered = true
    }

    if (this.p.proTrialTopBar === false || this.s.proTrialTopBar === false) proTrialTopBar = false
    if (requireUser && !currentUser) showSignIn = true

    if (menuTriggered) {
      actualShowMenu = true
    } else if (!currentUser || !menu || !showMenu) {
      actualShowMenu = false
    } else {
      actualShowMenu = true
    }

    let paddingLeft

    if (menu && actualShowMenu) {
      if (xlUp) {
        paddingLeft = 310
      } else if (mdUp) {
        paddingLeft = 290
      } else {
        paddingLeft = 220
      }
    }

    if (showSignIn) {
      logoAlign = "middle"
      logoWidth = 143
      logoHeight = 60
    } else if (mdUp) {
      logoAlign = "topLeft"
      logoWidth = 165
      logoHeight = 57
    } else {
      logoAlign = "middle"
      logoWidth = 116
      logoHeight = 40
    }

    return (
      <WithDefaultStyle style={defaultStyle}>
        <ImageBackground
          dataSet={actualDataSet}
          resizeMode="cover"
          source={this.s.background || background}
          style={imageBackgroundStyle}
        >
          <ClickOutsideView style={this.clickOutsideViewStyle ||= {width: "100%", height: "100%"}} testID="clickOutsideView">
            {proTrialTopBar &&
              <ProTopBar onLayout={this.tt.onProTopBarLayout} onUpgradePressed={this.tt.onUpgradePressed} />
            }
            {this.s.showUpgrade &&
              <ProUpgradeOverlay onRequestClose={this.tt.onRequestClosePlansSignUpModal} schoolClass={schoolClass} />
            }
            {this.s.showExpiredTrialFirstVisitUpgradeOverlay &&
              <ProUpgradeOverlay
                mainContentIdentifier="pro/upgrade-overlay/trial-expired-first-platform-visit-main-content"
                onRequestClose={this.tt.onRequestCloseExpiredTrialFirstVisitUpgradeOverlay}
                schoolClass={schoolClass}
              />
            }
            {bottomBar &&
              <View
                dataSet={this.bottomBarContainerDataSet ||= {class: "bottom-bar-container"}}
                onLayout={this.tt.onBottomBarContainerLayout}
                style={this.bottomBarContainerStyle ||= {
                  position: "fixed",
                  bottom: 0,
                  zIndex: 9,
                  width: "100%",
                  backgroundColor: "rgba(1, 1, 1, 0.5)",
                  padding: 20
                }}
              >
                <WithDefaultStyle style={this.bottomBarContainerDefaultStyle ||= {Text: {color: "#000"}}}>
                  {bottomBar}
                </WithDefaultStyle>
              </View>
            }
            <Animated.View
              dataSet={this.forceSizeContainerDataSet ||= {class: "force-size-container"}}
              style={this.cache("forceSizeContainerStyle", {
                width: "100%",
                height: "100%",
                minWidth: windowWidth,
                minHeight: windowHeight,
                alignItems: centered ? "center" : undefined,
                justifyContent: centered ? "center" : undefined,
                paddingLeft,
                paddingBottom: bottomBarHeight
              }, [centered, paddingLeft, bottomBarHeight])}
            >
              {currentUser && menu && !showMenu &&
                <Pressable onPress={this.tt.onMenuButtonPress} style={this.menuButtonStyle ||= {position: "absolute", zIndex: 9, top: 20, left: 20}}>
                  <Icon name="bars" size={24} />
                </Pressable>
              }
              {actualShowMenu &&
                <Menu
                  active={active || this.s.active}
                  anythingPro={this.tt.anythingPro}
                  anythingProState={this.tt.anythingProState}
                  currentUser={currentUser}
                  hasPro={this.tt.hasPro}
                  onRequestClose={this.tt.onMenuRequestClose}
                  onSignedOut={this.tt.onSignedOut}
                  onUpgradePressed={this.tt.onUpgradePressed}
                  showMenu={showMenu}
                  userSubscriptionPlanIdentifier={this.tt.userSubscriptionPlanIdentifier}
                />
              }
              {logo && (!menu || !actualShowMenu) &&
                <View
                  dataSet={this.logoContainerDataSet ||= {class: "chalky-layout-logo-container"}}
                  style={this.cache("logoContainerStyle", {
                    position: (mdUp && logoAlign != "middle") ? "absolute" : undefined,
                    top: mdUp ? 80 : undefined,
                    marginTop: !mdUp ? 40 : undefined,
                    width: "100%",
                    maxWidth
                  }, [logoAlign, mdUp])}
                >
                  <Image
                    dataSet={this.logoDataSet ||= {class: "chalky-layout-logo"}}
                    resizeMode="contain"
                    source={LogoImage}
                    style={this.cache("logoStyle", {
                      position: logoAlign == "topLeft" ? "absolute" : undefined,
                      top: logoAlign == "topLeft" ? 0 : undefined,
                      left: logoAlign == "topLeft" ? 33 : undefined,
                      width: logoWidth,
                      height: logoHeight,
                      marginHorizontal: logoAlign == "middle" ? "auto" : undefined,
                      marginBottom: logoAlign == "middle" ? 48 : undefined
                    }, [logoAlign, logoHeight, logoWidth, mdUp])}
                  />
                </View>
              }
              <View
                dataSet={this.chalkyLayoutContainerDataSet ||= {class: "chalky-layout-container"}}
                style={this.cache("layoutContainerStyle", {
                  padding: mdUp ? 40 : 10,
                  width: "100%",
                  maxWidth
                }, [maxWidth, mdUp])}
              >
                {currentUser && (actions || topLeft || topRight || userMenu || weekBar) &&
                  <View
                    style={this.cache("userBarStyle", {
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: mdUp ? "end" : "center",
                      marginTop: mdUp ? 20 : 50
                    }, [mdUp])}
                  >
                    {topLeft &&
                      <View style={this.topLeftContainerStyle ||= {marginRight: "auto"}}>
                        {topLeft}
                      </View>
                    }
                    {actions &&
                      <View style={this.actionsContainerStyle ||= {marginRight: 15}}>
                        {actions}
                      </View>
                    }
                    {topRight &&
                      <View style={this.topRightContainerStyle ||= {marginRight: 15}}>
                        {topRight}
                      </View>
                    }
                    {weekBar &&
                      <WeekBar />
                    }
                    {userMenu &&
                      <>
                        <View style={this.userIconStyle ||= {marginLeft: 16}}>
                          <Icon color="#2c2c96" name="circle-user" size={20} version="FontAwesome6" />
                        </View>
                        <Link to={Routes.userProfilePath()}>
                          <Text
                            dataSet={this.userTextDataSet ||= {class: "user-name-text"}}
                            style={this.userTextStyle ||= {marginLeft: 8, color: "#2c2c96", fontSize: 16, fontWeight: 700}}
                          >
                            {(() => {
                              if (mdUp) {
                                return currentUser.name()
                              } else if (smUp) {
                                return currentUser.name().split(/\s+/)[0]
                              } else {
                                return ""
                              }
                            })()}
                          </Text>
                        </Link>
                      </>
                    }
                  </View>
                }
                <View
                  dataSet={this.contentContainerDataSet ||= {class: "content-container"}}
                  style={this.contentContainerStyle ||= {
                    marginTop: 38
                  }}
                >
                  {showSignIn &&
                    <View style={this.signInContainerStyle ||= {alignItems: "center", justifyContent: "center"}}>
                      <SignIn redirectAfterSignIn={this.p.redirectAfterSignIn} />
                    </View>
                  }
                  {!showSignIn &&
                    <>
                      {(headerTitle || headerRight) &&
                        <View style={{flexDirection: "row", alignItems: "center"}}>
                          {headerTitle &&
                            <Text style={this.headerTitleStyle ||= {fontFamily: "DreamChaser", fontSize: 48}} testID="header-title">
                              {headerTitle}
                            </Text>
                          }
                          {headerRight &&
                            <View style={{marginLeft: "auto"}}>
                              {headerRight}
                            </View>
                          }
                        </View>
                      }
                      <FrontLayoutContext.Provider value={this.frontLayoutContext}>
                        {children}
                      </FrontLayoutContext.Provider>
                    </>
                  }
                </View>
              </View>
            </Animated.View>
          </ClickOutsideView>
        </ImageBackground>
      </WithDefaultStyle>
    )
  }

  frontLayoutContext = {
    setState: (states) => this.setState(states)
  }

  onBottomBarContainerLayout = (e) => this.setState({bottomBarHeight: e.nativeEvent.layout.height})

  onDimensionsChange = ({window}) => {
    this.windowWidth.setValue(window.width)
    this.windowHeight.setValue(window.height - this.tt.proTopBarHeight)
  }

  onMenuButtonPress = () => this.setState({menuTriggered: !this.s.menuTriggered})
  onMenuRequestClose = () => this.setState({menuTriggered: false})
  onSignedOut = () => this.setState({forceSignedOut: true, menuTriggered: false})
  onRequestClosePlansSignUpModal = () => this.setState({showUpgrade: false})

  onRequestCloseExpiredTrialFirstVisitUpgradeOverlay = async () => {
    try {
      if (this.currentUser?.contactId?.()) {
        const expiredTrialSubscription = await Subscription
          .ransack({
            contact_id_eq: this.currentUser.contactId(),
            plan_identifier_eq: "pro_trial",
            without_active_period: true
          })
          .first()

        if (expiredTrialSubscription) {
          await expiredTrialSubscription.update({expiredTrialPaywallShownAtFirstPlatformVisit: true})
        }
      }
    } catch (error) {
      console.error(error)
    }

    this.setState({showExpiredTrialFirstVisitUpgradeOverlay: false})
  }

  onProTopBarLayout = (e) => {
    this.proTopBarHeight = e?.nativeEvent?.layout?.height
    this.windowHeight.setValue(Dimensions.get("window").height - this.proTopBarHeight)
  }

  onUpgradePressed = () => {
    this.setState({showUpgrade: true})
  }

  setDocumentTitle() {
    const headTitle = this.s.headTitle || this.s.headerTitle

    if (headTitle) {
      document.title = headTitle
    } else {
      document.title = "WOOF"
    }
  }
}

export default memo(shapeComponent(ChalkyLayout))
